import { MaterialsListMaterial } from '@/api/queries/project/useGetProjectMaterialListSummaryItems';
import {
  FormStageStage,
  TFormState,
} from '@/ui/components/createPurchaseOrderSidebar/CreatePurchaseOrderSidebar';
import {
  MaterialCardPropsWithTransition,
  MaterialCardWithTransition,
} from '@/ui/components/materialCard/MaterialCard';
import SupplierSelectionControl from '@/ui/components/supplierSelectionControl/SupplierSelectionControl';
import { parseFulfillments } from '@/ui/pages/projectPage/components/MaterialsPanel/LiveAvailabilityMaterialSupplierBadge/fulfillment';
import LiveBranchStockMaterialSupplierBadge from '@/ui/pages/projectPage/components/MaterialsPanel/LiveAvailabilityMaterialSupplierBadge/LiveBranchStockMaterialSupplierBadge';
import MaterialSuppliersBadges from '@/ui/pages/projectPage/components/MaterialSuppliersBadges';
import {
  Alert,
  Button,
  Conditional,
  EmptyState,
  Field,
} from '@payaca/ambrosia';
import List from '@payaca/components/list/List';

import TagSelectCombobox from '@payaca/components/tagSelectCombobox/TagSelectCombobox';
import { useScroll } from '@payaca/hooks';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import useGetMe from '../../../../api/queries/me/useGetMe';
import { GetProjectMaterialsListOrderSummaryItemsQuery } from '../../../../gql/graphql';

// check supplier and account support direct purchase order
const isDirectPurchaseOrderSupportedForSupplierAndAccount = (
  supplier?: GetProjectMaterialsListOrderSummaryItemsQuery['projectById']['materialsList']['materialsListOrderSummaryItems']['items'][0]['materialsListMaterial']['material']['suppliedBy'][0]['supplier']
) => {
  return (
    supplier &&
    supplier?.isDirectPurchaseOrderSupported &&
    !!supplier?.accountConnectionMetadata && // bit crude but only relevant for cp now
    supplier.accountConnectionMetadata.accountType === 'credit'
  );
};
export interface IProps {
  formStage: FormStageStage;
  materialListMaterials: MaterialsListMaterial[];
  onAction: () => void;
  actionDisabled?: boolean;
  hideList?: boolean;
}

const MaterialsToOrderList: FC<IProps> = (props) => {
  const {
    formStage,
    materialListMaterials,
    onAction,
    actionDisabled = false,
    hideList = false,
  } = props;
  const { data: me } = useGetMe();

  /**
   * state
   */
  const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);
  const { y: scrollY } = useScroll({ current: scrollRef });

  /**
   * use form methods
   */
  const formMethods = useFormContext<TFormState>();
  const selectedSupplierId = formMethods.watch('supplierId');
  const selectedTagIds = formMethods.watch('tagIds');
  const selectedMaterials = formMethods.watch('materials');

  useEffect(() => {
    if (selectedSupplierId) {
      const filteredMaterialListMaterials = materialListMaterials.filter(
        (materialListMaterial) => {
          const conditions = [];

          conditions.push(
            materialListMaterial.materialsListMaterial.material.suppliedBy.some(
              (suppliedBy) =>
                suppliedBy.supplier.id === selectedSupplierId.toString()
            )
          );

          if (selectedTagIds?.length) {
            conditions.push(
              materialListMaterial.materialsListMaterial.material.tags.some(
                (tag) => selectedTagIds.includes(+tag.id)
              )
            );
          }

          return conditions.every(Boolean);
        }
      );

      formMethods.setValue(
        'materials',
        filteredMaterialListMaterials.map((filteredMaterialListMaterial) => ({
          ...filteredMaterialListMaterial,
          purchaseOrderQuantity: filteredMaterialListMaterial.materialQuantity,
          selectedQuantity: filteredMaterialListMaterial.materialQuantity,
        }))
      );
    } else {
      formMethods.setValue('materials', []);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'materialListMaterials', and 'formMethods'
  }, [selectedSupplierId, selectedTagIds]);

  useEffect(() => {
    const selectedSupplier = allAvailableSuppliers.find(
      (s) => +s.id === selectedSupplierId
    );

    const isDirectPurchaseOrderSupported =
      isDirectPurchaseOrderSupportedForSupplierAndAccount(selectedSupplier);
    if (isDirectPurchaseOrderSupported) {
      if (me?.user.id) {
        formMethods.setValue('userContactId', me.user.id);
      }
      formMethods.setValue('type', 'direct-asap');
    } else {
      formMethods.setValue('type', 'indirect');
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'me.user.id', 'allAvailableSuppliers', and 'formMethods'
  }, [selectedSupplierId]);

  const allAvailableSuppliers = materialListMaterials.flatMap(
    (materialListMaterial) => {
      return materialListMaterial.materialsListMaterial.material.suppliedBy.map(
        (suppliedBy) => suppliedBy.supplier
      );
    }
  );

  const allAvailableTags = [
    ...new Map(
      materialListMaterials
        .filter((materialListMaterial) =>
          materialListMaterial.materialsListMaterial.material.suppliedBy.some(
            (suppliedBy) => {
              if (!selectedSupplierId) {
                return false;
              }

              return suppliedBy.supplier.id === selectedSupplierId.toString();
            }
          )
        )
        .flatMap(
          (materialListMaterial) =>
            materialListMaterial.materialsListMaterial.material.tags
        )
        .map((tag) => [tag.id, tag])
    ).values(),
  ];

  const allAvailableSupplierIds = allAvailableSuppliers.map((s) => s.id);

  const hasBottomScrollShadow = !(
    scrollRef &&
    scrollRef.scrollHeight - scrollRef.scrollTop === scrollRef.clientHeight
  );

  const actionButtonDisabled = actionDisabled || selectedMaterials.length === 0;
  const panelOnLhs =
    formStage === 'INDIRECT_PURCHASE_ORDER_DETAILS' ||
    formStage === 'FULFILLMENT_OPTIONS';

  return (
    <div
      className={clstx(
        'z-10 overflow-auto flex flex-col h-full transition-colors duration-700 border-l',
        formStage === 'MATERIAL_LIST_MATERIALS' && 'bg-gray-50',
        panelOnLhs && 'bg-white'
      )}
      ref={setScrollRef}
    >
      <div
        className={clstx(
          'p-3.5 z-10 bg-white transition-colors duration-700 sticky top-0',
          formStage === 'MATERIAL_LIST_MATERIALS' && 'bg-gray-50',
          panelOnLhs && 'bg-white',
          scrollY > 0 && 'shadow',
          hideList && 'hidden'
        )}
      >
        <Field.Legacy className="mb-2.5">
          <Field.Label>Supplier you wish to order from</Field.Label>

          <Controller
            control={formMethods.control}
            name="supplierId"
            render={({ field: { onChange, value } }) => {
              return (
                <SupplierSelectionControl
                  canChangeSupplier={formStage === 'MATERIAL_LIST_MATERIALS'}
                  selectedSupplierId={value}
                  availableSupplierIds={allAvailableSupplierIds}
                  onChange={(newSupplierId) => {
                    onChange(newSupplierId || undefined);
                  }}
                />
              );
            }}
          />
        </Field.Legacy>

        <Conditional condition={!!selectedSupplierId}>
          <Controller
            name="tagIds"
            render={({ field: { value, onChange, name } }) => {
              return (
                <Field name={name} className="mb-2.5">
                  <Field.Label>Filter by tags</Field.Label>

                  <Conditional
                    fallback={
                      <Alert colour="yellow">
                        <p>Add tags to your Materials to filter by them</p>
                      </Alert>
                    }
                    condition={allAvailableTags.length > 0}
                  >
                    <TagSelectCombobox
                      multiple
                      value={value}
                      onChange={onChange}
                      availableTags={allAvailableTags.map((tag) => {
                        return {
                          id: +tag.id,
                          tagText: tag.name,
                          tagColour: tag.colour || '#000000',
                        };
                      })}
                    />
                  </Conditional>
                </Field>
              );
            }}
          />
        </Conditional>

        <Conditional condition={selectedMaterials.length !== 0}>
          <div className="flex justify-between items-center">
            <h4>Materials to include on order</h4>

            <Conditional condition={panelOnLhs}>
              <Button
                variant={'link-inline'}
                disabled={actionButtonDisabled}
                onClick={actionButtonDisabled ? undefined : onAction}
              >
                Edit list
              </Button>
            </Conditional>
          </div>
        </Conditional>
      </div>

      <List
        className="px-3.5"
        items={selectedMaterials}
        item={(material) => {
          const orderedMaterialSuppliers =
            material.materialsListMaterial.material.suppliedBy.sort((a, b) => {
              if (!selectedSupplierId) {
                return 0;
              }

              return a.supplier.id === selectedSupplierId.toString() ? -1 : 1;
            });

          let materialCardProps: MaterialCardPropsWithTransition = {
            show: material.selectedQuantity > 0,
            afterLeave: () => {
              formMethods.setValue(
                'materials',
                selectedMaterials.filter(
                  (selectedMaterial) => selectedMaterial.selectedQuantity > 0
                )
              );
            },
            name: material.materialsListMaterial.material.name,
            price: orderedMaterialSuppliers[0]?.price.unitPriceExcTax,
            tags: material.materialsListMaterial.material.tags,
            thumbnailUrl:
              material.materialsListMaterial.material.image?.thumbnailUrl ||
              undefined,
          };

          if (formStage === 'MATERIAL_LIST_MATERIALS') {
            materialCardProps = {
              ...materialCardProps,
              quantity: material.selectedQuantity,
              maxQuantity: material.materialQuantity,
              onChangeQuantity: (newQuantity) => {
                if (
                  newQuantity > material.materialQuantity ||
                  newQuantity < 0
                ) {
                  return;
                }

                formMethods.setValue(
                  'materials',
                  selectedMaterials.map((selectedMaterial) => {
                    return selectedMaterial.id === material.id
                      ? {
                          ...selectedMaterial,
                          selectedQuantity: newQuantity,
                          purchaseOrderQuantity: newQuantity,
                        }
                      : selectedMaterial;
                  })
                );
              },
            };
          }

          return (
            <MaterialCardWithTransition
              key={material.id}
              childrenClassName={clstx(
                panelOnLhs && 'w-full flex justify-between'
              )}
              {...materialCardProps}
            >
              <MaterialSuppliersBadges
                suppliers={orderedMaterialSuppliers.map((s) => ({
                  id: s.supplier.id,
                  name: s.supplier.name,
                }))}
                LiveAvailabilityMaterialSupplierBadge={
                  LiveBranchStockMaterialSupplierBadge
                }
                maxSuppliersToShow={1}
                fulfillments={parseFulfillments(
                  material.materialsListMaterial.fulfillments
                )}
              />

              <Conditional condition={panelOnLhs}>
                <p>Qty: {material.purchaseOrderQuantity}</p>
              </Conditional>
            </MaterialCardWithTransition>
          );
        }}
      />

      <Conditional condition={!selectedSupplierId}>
        <EmptyState
          className="mb-auto"
          text="Start by selecting a Supplier"
          iconName="arrow-block-up.3"
        />
      </Conditional>

      <Conditional
        condition={!!selectedSupplierId && selectedMaterials.length === 0}
      >
        <EmptyState className="my-auto" text="No Materials on order" />
      </Conditional>

      <Conditional condition={formStage === 'MATERIAL_LIST_MATERIALS'}>
        <div
          className={clstx(
            'p-3.5 z-10 transition-colors duration-700 sticky bottom-0 shadow bg-gray-50',
            hasBottomScrollShadow && 'shadow-flipped-lg',
            selectedSupplierId && selectedMaterials.length > 0 && 'mt-auto'
          )}
        >
          <Button
            className="w-full"
            disabled={actionButtonDisabled}
            variant={'solid'}
            onClick={actionButtonDisabled ? undefined : onAction}
          >
            Proceed
          </Button>
        </div>
      </Conditional>
    </div>
  );
};

export default MaterialsToOrderList;
